"use client";

import type { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { DoorOpen, MapPin } from "lucide-react";
import React from "react";

interface RequestRoomsPanelProps {
  rooms: IResource[];
  /** Room currently highlighted on the map above. */
  selectedRoomId: string | null;
  onSelectRoom: (roomId: string) => void;
  officeName: string;
}

/**
 * The rooms of the reservation a request is attached to. A reservation can hold several
 * meeting rooms, and the map above shows one at a time — picking a row moves the map to
 * that room.
 */
export const RequestRoomsPanel: React.FC<RequestRoomsPanelProps> = ({
  rooms,
  selectedRoomId,
  onSelectRoom,
  officeName
}) => {
  if (rooms.length === 0) return null;

  return (
    <section className="mt-6">
      <h3 className="mb-2 font-medium text-neutral-500 text-xs uppercase tracking-wide">
        Rooms · {rooms.length}
      </h3>
      <div className="flex flex-col gap-2">
        {rooms.map((room) => {
          const isSelected = room.id === selectedRoomId;
          const capacity = room.total_slots;
          const subtitle = [capacity ? `${capacity} people` : null, officeName]
            .filter(Boolean)
            .join(" · ");

          return (
            <button
              key={room.id}
              type="button"
              onClick={() => onSelectRoom(room.id)}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                isSelected
                  ? "border-primary bg-primary/5"
                  : "border-neutral-200 hover:border-neutral-300"
              )}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary">
                <DoorOpen className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-neutral-900 text-sm">
                  {room.name}
                </span>
                <span className="block truncate text-neutral-500 text-xs">
                  {subtitle}
                </span>
              </span>
              {isSelected && (
                <span className="flex shrink-0 items-center gap-1 font-medium text-primary text-xs uppercase">
                  <MapPin className="h-4 w-4" />
                  On map
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};
